// src/components/operationCenter/widgets/NotepadWidget.tsx
// Notepad widget with markdown-style notes and date headers

import React, { useState, useEffect, useRef, useCallback } from "react";
import { StickyNote } from "lucide-react";
import type { WidgetProps, NotepadConfig } from "@/types/dashboard";
import { useDashboardStore } from "@/stores/dashboardStore";

const NotepadWidget: React.FC<WidgetProps<NotepadConfig>> = ({ widget }) => {
  const config = widget.config as NotepadConfig;
  const updateWidgetConfig = useDashboardStore((s) => s.updateWidgetConfig);

  // Local state for immediate UI updates
  const [content, setContent] = useState(config.content ?? "");
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state with config when config changes externally
  useEffect(() => {
    setContent(config.content ?? "");
  }, [config.content]);

  // Save function
  const saveContent = useCallback(
    (newContent: string) => {
      updateWidgetConfig(widget.id, { content: newContent });
    },
    [updateWidgetConfig, widget.id]
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);

    // Debounce save: clear existing timeout and set new one
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveContent(newContent);
    }, 500);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <StickyNote className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
        <span className="text-xs uppercase tracking-wide text-slate-300">
          {widget.title}
        </span>
        <span className="ml-auto text-[10px] text-slate-500">Auto-saved</span>
      </div>

      {/* Editor */}
      <textarea
        value={content}
        onChange={handleChange}
        placeholder="Write your notes here..."
        className="flex-1 w-full p-3 bg-transparent text-slate-200 text-sm resize-none focus:outline-none placeholder:text-slate-500 leading-relaxed"
        spellCheck={false}
      />
    </div>
  );
};

export default NotepadWidget;
